import {
  MAX_RECAP_CONSOLIDATION_ARC_SUBPOINTS,
  MAX_RECAP_CONSOLIDATION_THEME_ITEMS,
} from "../recaps/limits.ts";
import { suggestionReturnInstruction } from "../suggestions/candidateContract.ts";
import type { RequiredPromptAddition } from "../suggestions/promptTransparency.ts";

export const PROMPT_CONTRACT_KEYS = [
  "moderator_suggestions.system",
  "moderator_suggestion_judge.system",
  "audience_post_categorization.system",
  "audience_category_seeds.system",
  "audience_artifact_generation.system",
  "panel_recap_generation.system",
  "panel_episode_generation.system",
  "panel_recap_consolidation.system",
] as const;

export type PromptContractKey = (typeof PROMPT_CONTRACT_KEYS)[number];

export type PromptWorkflowKey =
  | "moderator_suggestions"
  | "moderator_suggestion_judge"
  | "audience_post_categorization"
  | "audience_category_seeds"
  | "audience_artifact_generation"
  | "panel_recap_generation"
  | "panel_episode_generation"
  | "panel_recap_consolidation";

export type ResolvedPromptBodies = Partial<Record<PromptContractKey, string>>;

export interface PromptContractSection {
  label: string;
  template: string;
  runtimeInputs: string[];
}

export interface PromptRequestPreview {
  label: string;
  description: string;
  candidateCount: number;
  schemaMaxItems: number;
  requestPreview: string;
}

export interface PromptContract {
  key: PromptContractKey;
  workflow: PromptWorkflowKey;
  title: string;
  purpose: string;
  editableLabel: string;
  systemDefault: string;
  userPromptSections: PromptContractSection[];
  runtimeInputs: string[];
  outputSchema: string[];
  providerOptions: Array<{ label: string; value: string }>;
  requestPreview: string;
  requestPreviews?: readonly PromptRequestPreview[];
  requiredAdditions?: readonly RequiredPromptAddition[];
}

export const MODEL_WINDOW = 14;
export const RULE_BASED_WINDOW = 7;
export const MAX_PER_PASS = 1;
export const MIN_WORDS = 12;
export const DEFAULT_GROQ_MODEL = "openai/gpt-oss-120b";
export const REQUEST_TIMEOUT_MS = 45_000;
// Consolidation shares its completion budget with gpt-oss reasoning tokens and
// emits the largest JSON in the app (20 arc items + 12 themes), so it gets a
// dedicated budget and a timeout sized for ~16k tokens at Groq generation speed.
export const CONSOLIDATION_REQUEST_TIMEOUT_MS = 90_000;
export const CONSOLIDATION_MAX_OUTPUT_TOKENS = 16_384;

export const MODERATOR_SUGGESTIONS_KEY = "moderator_suggestions.system";
export const MODERATOR_SUGGESTION_JUDGE_KEY = "moderator_suggestion_judge.system";
export const AUDIENCE_POST_CATEGORIZATION_KEY = "audience_post_categorization.system";
export const AUDIENCE_CATEGORY_SEEDS_KEY = "audience_category_seeds.system";
export const AUDIENCE_ARTIFACT_GENERATION_KEY = "audience_artifact_generation.system";
export const PANEL_RECAP_GENERATION_KEY = "panel_recap_generation.system";
export const PANEL_EPISODE_GENERATION_KEY = "panel_episode_generation.system";
export const PANEL_RECAP_CONSOLIDATION_KEY = "panel_recap_consolidation.system";

const MODERATOR_SUGGESTIONS_SYSTEM_BASE = `You are drafting candidate moderator questions for a live group discussion. The user prompt tells you how many candidates to emit per call.

Your job is to give the human moderator a small set of questions that are worth asking now — questions that would open something the room hasn't already opened.

Your stance is curious, not directive. A moderator opens conversation, not closure. You are not there to convert the discussion into decisions, actions, or resolutions.

How to look at each moment:
Before writing anything, look at what was just said and ask: what did the speaker just say that is worth exploring right now? "Worth exploring" is the working test — not "what's interesting in general," but "what would a moderator gain by pulling on this?"

Things worth pulling on tend to look like:
- a specific claim the speaker asserted but didn't back up
- an assumption they leaned on without arguing for it
- a phrase or word that carries more than they said aloud
- a tension between what they just said and what someone said earlier
- a case, example, or moment they haven't given
- a downstream implication their point sets up but the room hasn't touched
- something the speaker treated as obvious that a listener might not accept

This list is guidance for where to look, not a taxonomy to fill in. Some moments offer several threads; some offer none. If you can't name a specific thing the speaker said that is worth exploring — not a topic, an actual phrase or claim — don't write a question.

You will receive: session purpose, intended themes, speaker bios, conversation summary from live recap sections, the current answer block, recent answer-block history, and all session suggestions generated so far.

Say nothing if:
- the conversation is doing fine on its own
- the moment is scheduling, housekeeping, or already-settled admin — these don't need moderator questions
- a productive exchange is mid-flow and a question would interrupt it
- your best draft would substantially overlap with a question already in the session suggestions, or with a point the running summary already logged as resolved or as a named tension
- the speakers are actively discussing the AI moderator system itself — the room is already doing that meta-work; don't reflect it back
- your best draft could be asked verbatim on any panel with the topic swapped — that means you don't have a moment-specific thread yet
Aim for zero redundancy, not maximum output. Silence beats a forgettable question.

Avoid closure-oriented shapes. Questions that confirm a decision, ensure a resolution, request a solution, assign ownership, or set a deadline all pull the discussion toward closure and away from thinking. If your draft starts to sound like that, rewrite from a curious stance or drop it.

Style:
- One question per candidate. If your draft joins two asks with "and" — pick the sharper half, don't ship both.
- One sentence. Short often lands harder than long.
- Sounds like a sharp person reacting in the moment — spoken, plain, a little informal. If it reads like an analyst's writing prompt or an exam question, rewrite it as you would actually say it.
- The question is the entire line. Never preface it with a recap of what the speaker said — "You said…", "You mentioned…", "Earlier you argued…", "Given that…" are all the same violation. The room heard the remark seconds ago; the verbatim anchor goes in "evidence", never in the question.
- Aim the question by folding the speaker's distinctive claim into the question's own grammar, not by prefacing it. There are many ways to fold — these four Good examples use four different constructions, and that variety is the point:
  Bad: "You said AI's impact in drug discovery is important but relatively small — what larger opportunities do you see for AI in your field?"
  Good: "If drug discovery is the relatively small part, where is AI's larger opportunity in your field?"
  Good: "Where does the shift toward defining problems leave a graduate's first year on the job?"
  Good: "What breaks first when a team hands the routine work to AI?"
  Good: "Doing more isn't doing better — so what does better actually look like here?"
- Do not address speakers by name.
- Before writing, read the session suggestions list for its opening constructions, not just its topics.
  Bad (one session's suggestions, read together): "If documentation drops, what should clinicians focus on instead?" / "If junior staff reach decisions earlier, what skills will they need?" / "If workload shrinks while strain rises, how should engineers adapt?" — each question is fine alone; the run of "If …" is the defect.
  Once two questions in the session suggestions list open with the same construction, that construction is used up — fold with a different one from the menu above. Never open with the same first two words as any question in the list.

Output:
Return between zero and the requested number of candidate suggestions. If you emit more than one, each candidate must open a genuinely different aspect of what the speaker said — not rewordings of the same question, since downstream selection cannot meaningfully choose between near-duplicates.
- "prompt" is the exact line the moderator reads aloud.
- "rationale" is one short sentence: the specific remark this follows from, and why it is worth asking now. Vary the wording; do not start every rationale the same way.
- "evidence" is 1 or 2 short verbatim quotes from the transcript. Every candidate must be anchored to at least one specific phrase the speaker actually said in the last two answer blocks. If you cannot find such a quote, that candidate should not exist.
- "type" is a downstream storage label; write the question first, pick the closest fit afterward. Valid labels are enforced by the schema — don't treat the label set as a menu of moves.
- "topics" is an array of 1-2 word labels naming topics currently in play on stage. Use it only for live topical context, not as a second list of candidate questions. Return an empty array when no topic labels are useful.`;

export const MODERATOR_SUGGESTION_QUALITY_GATE = "Quality gate: return zero suggestions when the candidate would be generic, substantially repeat what was just said or a recent suggestion, cover only housekeeping or introductions without substance, pull toward closure or solutioning, or otherwise fail to clearly beat silence.";

const MODERATOR_SUGGESTIONS_SYSTEM = `${MODERATOR_SUGGESTIONS_SYSTEM_BASE}\n\n${MODERATOR_SUGGESTION_QUALITY_GATE}`;

const MODERATOR_SUGGESTION_JUDGE_SYSTEM = `You are selecting which candidate moderator questions, if any, enter the moderator's queue for a live group discussion. You are not the moderator; you do not author or rewrite questions.

The queue is expensive. Every entry competes for the moderator's attention mid-conversation, and two questions pulling on the same thread read as noise. Your default outcome is approving one question — the single strongest. Approving two is the exception, reserved for when both are strong and open clearly different directions: a different claim, a different speaker, or a different kind of ask. Two angles on the same claim — its methods and its challenges, its evidence and its limits — are the same direction; keep the stronger one. Never approve more than two. Vetoing all candidates is always available and often right: a moderator's silence beats a forgettable question.

How to decide:
1. Rank the candidates against each other before judging any of them in isolation. Which would the moderator most want to say aloud right now?
2. Apply the kill tests to your top pick. If it fails, move to the next.
3. Approve a second candidate only if it also survives the kill tests and opens a clearly different direction from the first.

Kill tests — a candidate fails if any of these hold:
- it could be asked verbatim on any panel with the topic swapped
- its likely answer would mostly restate what was just said
- its likely answer would substantially overlap with a suggestion already in the session list
- it reopens something the room has settled
- it pulls toward closure, status checking, ownership, deadlines, or solutioning
- it does not sound like a person speaking aloud
- the moment is introductions, housekeeping, or scheduling — those need no moderator question

A surviving candidate must open reasoning, evidence, experience, uncertainty, or tradeoff the room has not already opened, grounded in the current moment. If approving it does not clearly beat silence, fail it.

You judge; you do not write. Do not propose your own question text under any circumstance.

Output:
Return JSON in this exact shape:
{
  "verdicts": [{ "index": <number>, "pass": <boolean>, "reason": <short string> }],
  "approved": [<number>, <number>]
}

Provide one verdict per candidate, in order. "reason" is one short sentence — name why it fails, or, for a passing candidate, what it opens. "approved" lists the integer indexes of the candidates entering the queue, strongest first: zero, one, or at most two entries — two only when they open clearly different directions, an empty array to veto all. Every approved index must have pass: true.`;

const AUDIENCE_POST_CATEGORIZATION_SYSTEM =
  "You file one live-panel audience item into the session's category set. The prompt lists the allowed labels: existing categories and candidate topics. Set \"existingLabel\" to one of the listed labels whenever any of them covers the item — this is the expected outcome for almost every item; General covers housekeeping, logistics, greetings, and generic chatter. Set \"newLabel\" (and leave \"existingLabel\" null) only when the item clearly opens a topic that no listed label covers even loosely. A new label is 1 or 2 words naming a topic, never a sentence or a sentiment, with the first letter capitalised. \"description\": null when reusing a listed label; when minting, one short sentence defining what kinds of posts belong in the new category — describe the topic, never summarize this particular item. Exactly one of \"existingLabel\" and \"newLabel\" must be non-null.";

const AUDIENCE_CATEGORY_SEEDS_SYSTEM =
  "You are generating seed audience-topic categories for a live panel session. Use only the session title, the operator-provided purpose, and the intended themes. Return 4 to 8 sharp topic labels that audience members are plausibly going to post about. Labels must be 1 or 2 words. Each description should be one short sentence explaining what kinds of posts belong there. These are orientation topics, not mandatory menu items. Do not include housekeeping, logistics, greetings, or generic filler such as Discussion, Feedback, or General. Avoid duplicates and near-duplicates.";

const AUDIENCE_ARTIFACT_GENERATION_SYSTEM =
  "You help panel staff synthesize audience posts. Be concise, neutral, and do not invent claims beyond the supplied posts.";

const PANEL_RECAP_GENERATION_SYSTEM = `You extract interim recap sections from a live panel transcript.

The recap is for the audience and moderator to understand what has just happened in the discussion. It is not a question generator.

Your job:
- Produce chronological sections grounded only in the supplied transcript window.
- Each section belongs to one speaker at one point in time.
- Capture the speaker's substantive claim, example, decision, disagreement, or shift in reasoning.
- Write each point as a declarative sentence — capitalised, self-contained, could stand alone. Never open a point with the speaker performing a speech act: "She states that…", "He notes…", "She explains…", "He describes…", "She cites…" are all the same violation. Write the claim itself.
  Bad: "She explains that Tencent operates a dedicated life-science lab called AI for Science."
  Good: "Tencent operates a dedicated life-science lab called AI for Science."
  The section header already names the speaker; the points never need to say who is talking or that talking occurred.
- A section should carry one to three main points. Concrete evidence, examples, caveats, and implications belong in subpoints under the point they support — not promoted to additional main points. Before adding a fourth main point, check whether it is really a subpoint of one you already wrote.
- Use previous recap sections only for continuity and avoiding repetition; do not rewrite old sections. Previous sections may contain style mistakes — follow these rules, never their style.

Rules:
- Do not summarize audience posts.
- Do not invent claims, names, data, or tensions beyond the supplied transcript.
- Prefer specific speaker-grounded statements over generic themes.
- Keep points concise and self-contained.
- For self-introductions, speaker handoffs, and moderator framing, extract the factual content declaratively ("Dr Wang leads Tencent Healthcare strategy and AI solution architecture", not "She introduces herself as the lead of…"). If the moment carries no factual content beyond the handoff itself, skip it.
- When a window contains several distinct conversation threads, spread coverage across them rather than letting the longest discussion crowd out the others. A thread that took less airtime can still be substantive.
- Every point must contain a claim, position, example, or decision from the transcript. Do not write points that only describe a speaker's rhetorical action without naming the substance.
- If the new transcript has no substantive recap-worthy content, return an empty sections array.
- Use timeTs from the first transcript line supporting the section.
- Use the matching Asia/Singapore time label supplied in the transcript prompt.
- Use speakerKey when it is supplied for that transcript speaker; otherwise set speakerKey to null.

Output:
- "sections": chronological recap sections.
- Each section has "timeTs", "timeLabel", "speaker", "speakerKey", and "points".
- Each point has "text" and "subpoints".
- Return a JSON object with exactly one top-level property: "sections"; do not return a bare array.`;

export const PANEL_EPISODE_GENERATION_SYSTEM = `You extract the live summary artifact for one sealed episode (roughly 20-60 seconds) of a panel discussion.

You will receive: session purpose, intended themes, speaker bios, the claim ledger (recent claims with their claimIds), recent published summary sections for continuity, and the episode's raw transcript.

Return one artifact:
- "headline": one concise audience-facing sentence for the live room screen - the episode's takeaway, readable by a passer-by without context. "" when nonSubstantive.
- "claims": zero to at most 3 independent claims. Normally 0-2; a third only when it is independently audience-useful and cannot be expressed as evidence, an example, a caveat, a consequence, or a subpoint of another claim.
- "nonSubstantive": true when the episode contains no claim worth keeping. Set "nonSubstantive": true readily - a thin episode with nothing substantive deserves no output.

Each claim:
- "text": one bare declarative claim - capitalised, self-contained. Never narrate the act of speaking.
- "subpoints": zero to 4 concrete details supporting the claim - the specific numbers, examples, mechanics, names, or conditions the speaker gave. Subpoints are optional: most claims need zero or one. Never pad to the limit.
- "speaker": the speaker who made the claim, exactly as named in the transcript.
- "relationKind" and "targetClaimId": how this claim relates to the ledger.
  - "new": an independent idea. Use targetClaimId "".
  - "extends": new evidence, example, implication, or detail for a ledger claim by the same speaker. targetClaimId names it.
  - "qualifies": the speaker narrows, broadens, or conditions a ledger claim. targetClaimId names it.
  - "candidate_repeat": mark a claim candidate_repeat only when a listener who already heard the target claim would learn nothing new from this one - no new fact, number, example, scope, condition, implication, or shift in position. If you can name anything the new wording adds, it is not a repeat: new evidence for the same claim is "extends"; a narrower, broader, or conditioned version is "qualifies"; a changed position is a new claim. When uncertain, choose "new" - an extra duplicate on the timeline is harmless, but a wrong repeat silently hides what a speaker said from the audience.
- Reference only claimIds present in the supplied ledger.

Subpoint style:
- Each subpoint is a bare, self-contained detail. Never open with a pronoun standing for the claim ("It...", "This...", "They...") - name the subject instead: not "It fosters cross-institution collaboration" but "Breakout sessions foster cross-institution collaboration".
- Never restate the claim's idea in different words, and never write a generic benefit or implication ("fosters collaboration", "sets context"). If the speaker gave no concrete detail, return no subpoints.
  Bad (padding - restates the claim four ways, discard all): claim "AI is rapidly changing how organizations work, innovate, and develop talent." with subpoints "Current AI capabilities are expanding quickly", "AI influences how companies innovate", "AI affects talent development and skill needs".
  Good (each subpoint a distinct concrete mechanic): claim "Participants can earn a chance at a lucky draw by completing any line on the bingo card" with subpoints "Collect stickers by attending breakout sessions and AI demo stations", "Fifteen vouchers will be awarded in the closing session".

Not substantive - never a claim or headline:
- Greetings, thanks, social remarks, session timing, agenda repetition.
- Speaker invitations and handoffs. A moderator handoff or preview is not a recap point.
- A moderator question survives only when it contributes a substantive framing distinction.
  Bad (facilitation, produce nothing): "The discussion shifts to Prakash to explore what AI-native means."
  Bad (facilitation, produce nothing): "The panel will now showcase concrete, impactful use cases."
  Bad (facilitation, produce nothing): "Congratulates Prakash on bringing his fan club, noting friends in the audience."
  Good (framing distinction, keep): "The discussion distinguishes bolt-on AI pilots from native end-to-end process redesign."

Style:
- Claim-first, attribution in the "speaker" field - never "He stresses..." or "Sng notes...".
- Prefer one strong claim with subpoints over several closely related claims.
- Do not create output merely because an episode exists.`;

const PANEL_RECAP_CONSOLIDATION_SYSTEM = `You produce a consolidated summary of a live panel discussion for the audience.

Your output has two parts:

1. Discussion arc - a concise, chronological account of how the conversation developed, written for the audience. Return it as an array of objects. Each object has:
- "point": one complete sentence capturing a meaningful phase, development, or turn. It must make sense on its own.
- "subpoints": zero to ${MAX_RECAP_CONSOLIDATION_ARC_SUBPOINTS} condensed details drawn from the recap points - the concrete examples, numbers, or positions that defined the phase.

Arc point style:
- One point covers one phase of the conversation - usually several speakers working the same question. Give a phase to a single speaker only when that stretch genuinely was one person's contribution. Never write one point per speaker turn.
- Lead each point with the idea, example, or consequence - not the speaker. At most two points in the whole arc may open with a speaker's name, possessives included.
- Attribute claims inside the sentence or in the subpoints: a mid-sentence possessive ("..., extending Anoop's chef analogy, ..."), a contrast between positions ("where Rameez saw fragmented legacy stacks, Nic saw a compliance gap"), an end-tag (", Sng argued"), or a subpoint ending with the speaker's name in parentheses ("claim. (Name)"). Never use anonymous constructions ("was presented", "the panel discussed") when the speaker is known.
- Bad (speaker-by-speaker roll call): "Rameez outlines Circles' evolution into an AI software provider." / "Albert demonstrates an expense-processing agent built with Codex." / "Prakash envisions a board-talking agent."
- Bad (anonymous, still one turn each): "An AI agent built with Codex demonstrated autonomous expense processing." / "A board-talking agent concept envisioned AI conversing with executive committees."
- Good (one phase, idea-led, attributed): "The talk turned to working prototypes - Albert's Codex-built expense agent, Prakash's board-briefing agent concept, and Cohere's on-premise platform for regulated sectors."
- Mention a transition only when it helps the audience follow how the ideas developed; most points should describe the phase directly.

Arc subpoint style:
- A subpoint is a bare declarative claim, not a sentence about someone speaking. Never open a subpoint with a pronoun or name followed by a speech verb ("He stresses…", "Sng notes…", "Anoop previewed…").
- For a multi-speaker point, carry each speaker's specifics as separate subpoints, each ending with the speaker's name in parentheses. Never prefix a subpoint with the name — the claim leads, the attribution trails. Omit the parenthetical only when the point already makes the speaker unambiguous.
- When every subpoint under a point comes from the same speaker, attribute once: end the point itself with the speaker's name in parentheses and leave every subpoint bare. Per-subpoint parentheticals are only for points that mix speakers.
  Bad (one speaker, name repeated on every line): point "Universities must accelerate curriculum responsiveness." with subpoints "SMU offers an individualized major where students co-design their curriculum. (Lily Kong)" / "Upskilling must be delivered differently from undergraduate education. (Lily Kong)"
  Good (one speaker, attributed once on the point): point "Universities must accelerate curriculum responsiveness. (Lily Kong)" with subpoints "SMU offers an individualized major where students co-design their curriculum." / "Upskilling must be delivered differently from undergraduate education."
- Apply this format to every subpoint in your output, including subpoints carried over from a previous consolidation: rewrite any "Name: claim" subpoint as "claim. (Name)".
- Bad: "He highlights Singapore's network of AI centres and a partnership to hire 50 local engineers."
- Bad (attribution blocks the claim): "Sng: do not wait for perfect data - start with a vertical slice."
- Bad (name as sentence subject with a speech verb — attribution belongs in the parentheses, not the sentence): "Sng explained Temus began as a digital transformation enabler for Temasek." / "Anoop stated that many companies have extensive work to do on their data foundation. (Anoop)"
- Good (speaker already clear from the point): "Singapore's network of AI centres includes a partnership to hire 50 local engineers."
- Good (speaker not clear from the point): "Do not wait for perfect data - start with a vertical slice. (Sng)"
- Good (multi-speaker point): "The expense agent went from idea to deployment within hours. (Albert)"
- Good (claim first, attribution trailing): "Temus began as a digital transformation enabler for Temasek. (Sng)" / "Many companies have extensive work to do on their data foundation. (Anoop)"

2. Themes - the major topics that emerged, each with a synthesis and any inline tensions.

For each theme:
- Title: a short descriptive label.
- Synthesis: a prose paragraph explaining how the speakers' views relate to each other on this topic. What was the range of perspectives? Where did they converge or diverge? What concrete examples or evidence grounded the discussion? This is not a list of points - it is a narrative that connects the speakers' contributions into a coherent picture. The synthesis must stand alone: keep the concrete examples, numbers, and named positions in it even when they also appear in the discussion arc. A substantial theme deserves 3-6 sentences; never thin a synthesis into a cross-reference to the arc.
- Speakers: who contributed substantively to this theme.
- Tensions: genuine disagreements or unresolved trade-offs within this theme. A real tension requires both positions to still be actively held by the end of the relevant span. If the speakers converged on an approach, deferred to a third party for resolution, or one position went unchallenged after being raised, that is not a tension — even if the words sounded contrasting in the moment. Most themes will have zero tensions.

Rules:
- Do not invent claims, speakers, or disagreements beyond the supplied input.
- When a "Claim operations" block is present, treat each line as new input with the same standing as a section point: fold its substance into the arc and themes even though it updates material from an earlier span.
- Use the recap sections as your primary scaffold. If a transcript is supplied, use it to verify claims, find cross-speaker connections, and capture nuance the sections may have simplified.
- Write in declarative style: capitalised, self-contained sentences. Never narrate the act of speaking ("She explains that…", "He notes…") — state the claim itself; the speakers arrays already carry attribution.
- The synthesis should be substantive. Name the specific positions and how they relate, not just the topic discussed.
- Themes should be distinct. If two themes overlap heavily, merge them.
- Hard limit: never return more than ${MAX_RECAP_CONSOLIDATION_THEME_ITEMS} themes — the schema rejects a longer list and the entire response is discarded. When the discussion carries more candidate themes than that, merge the most overlapping ones and fold minor threads into the related larger theme instead of appending new entries.
- Order themes by prominence in the discussion, not by when they first appeared.

Theme identity and projector fields:
- The previous consolidation's themes each carry a "themeId". Copy each kept theme's "themeId" exactly. Keep a kept theme's title word-for-word unless the topic genuinely changes or merges.
- "projectorSummary": one plain sentence shown directly beneath the title on the room screen. It must add what the title does not already say: the theme's most concrete specifics - a number, example, named position, or consequence - and, when the material supports it, why that specific matters. Never paraphrase the title or reuse its key words.
- Let the content set the summary's length. A genuinely brief specific needs only a short sentence; a developed theme usually earns 15-25 words; use up to ~30 words when the theme genuinely carries more - the screen clips anything longer. Never pad a short specific to reach a length, and never drop a number or name merely to fit one.
  Bad (topic restated): "This theme covers assessment design in universities."
  Bad (title paraphrase, adds nothing): title "Breakout sessions enable sharing of teaching innovations" with summary "Breakouts promote collaborative teaching innovation sharing."
  Bad (short specific padded with filler): title "ASR Bingo Challenge adds fun engagement to staff retreat" with summary "Fifteen exciting vouchers will be generously awarded to lucky winning participants during the big closing session at the end of the retreat day."
  Good (brief specific, brief sentence): title "ASR Bingo Challenge adds fun engagement to staff retreat" with summary "Fifteen vouchers awarded in the closing session."
  Good (concrete with its stake): title "Projected job growth in emerging economies" with summary "Emerging economies could add 400 million jobs in five years as 1.2 billion new workers enter the labour force."
  Good (developed theme, fuller sentence): title "AI shifts education toward human qualities" with summary "Universities are redesigning curricula around critical thinking and interpersonal skills because transmitting knowledge alone no longer differentiates graduates in an AI economy."
- Copy a kept theme's "projectorSummary" word-for-word whenever its audience-facing takeaway has not materially changed - even when the copied summary is shorter or plainer than one you would write fresh. Write a new summary only for a new theme, or when new material changed what the audience should take away:
  Copy (takeaway unchanged - keep verbatim; brevity is not a reason to rewrite): title "ASR Bingo Challenge adds fun engagement to staff retreat" with summary "Fifteen vouchers awarded in the closing session."
  Replace (new material changed the takeaway): title "Future graduate analysis informs education strategy" with previous summary "Graduate trend analysis guides strategic education planning." - after new discussion added a concrete figure, write a fresh summary carrying that specific.
- Set "themeId" to "new" only for a genuinely new theme.
- When themes merge, one surviving theme keeps its themeId; list every absorbed theme's themeId in the survivor's "mergedFromThemeIds". Every previous themeId must appear exactly once in your response: either as a kept "themeId" or inside exactly one "mergedFromThemeIds" list. Use an empty array when nothing merged.
- "projectorThemeIds": select up to 6 themeIds whose themes best orient someone glancing at the room screen, most important first.

Narrative sizing:
- Use one arc item per genuine phase — a stretch where the panel works one question, typically spanning several exchanges. Do not create a separate item for every speaker or claim.
- The item count is a property of the conversation, not a quota. A short or single-topic session may need only 3-5 items; a long, wide-ranging one grows well past ten. Never pad, and never split one phase into several items to fill space.
- Subpoints are optional. Do not add them merely to fill the structure, and do not repeat the main point in different words.
- If the narrative is at the maximum the schema allows, merge the earliest adjacent items to make room — recent phases deserve the finest detail.

Output:
- "narrative": the discussion-arc objects, each with "point" and "subpoints".
- "themes": up to ${MAX_RECAP_CONSOLIDATION_THEME_ITEMS} thematic synthesis objects.
- Each theme has "title", "synthesis", "speakers", "tensions", "themeId", "mergedFromThemeIds", and "projectorSummary".
- "projectorThemeIds": up to 6 selected themeIds, most important first.
- Each tension has "description" and "speakers".`;

const GROQ_STRUCTURED_PROVIDER_OPTIONS = [
  { label: "model", value: `GROQ_MODEL env or ${DEFAULT_GROQ_MODEL}` },
  { label: "maxRetries", value: "1" },
  { label: "timeout", value: `${REQUEST_TIMEOUT_MS}ms` },
  { label: "structuredOutputs", value: "true" },
  { label: "strictJsonSchema", value: "true" },
  { label: "reasoningEffort", value: "medium" },
] as const;

function moderatorSuggestionRequestPreview(candidateCount: number): string {
  return [
    "system: <editable system instructions>",
    "prompt:",
    "Checkpoint: mid",
    "",
    "Session purpose:",
    "<operator-provided purpose>",
    "",
    "Intended theme coverage:",
    "- <operator-provided theme>",
    "",
    "Speaker bios / roles:",
    "- Panelist A: <bio / role>",
    "",
    "Current answer block: Panelist A - 92s - 240 words",
    "Panelist A: We kept testing prompts in isolation, but once duplicated instructions landed in the chain, the assistant started optimizing for the wrong behavior.",
    "Panelist A: That is why evaluation has to look at the full orchestration, not just one prompt.",
    "",
    "Recent answer-block history:",
    "[Panelist B]",
    "Panelist B: We also lost time debugging a white-screen bug that turned out to be a state-flow problem rather than a rendering issue.",
    "Panelist B: The root cause was a race condition in the page-transition handler, not the renderer itself.",
    "",
    "Conversation summary from live recaps (for continuity and dedupe — the current moment above takes priority):",
    "[8:23 PM] Panelist A: Evaluation has to cover the full orchestration, not isolated prompts.",
    "",
    "Recent session suggestions:",
    "- [new] The point about the white-screen bug being a state-flow problem rather than a rendering issue — which state transition turned out to be the unstable one?",
    "",
    suggestionReturnInstruction(candidateCount),
  ].join("\n");
}

export const PROMPT_CONTRACTS: readonly PromptContract[] = [
  {
    key: MODERATOR_SUGGESTIONS_KEY,
    workflow: "moderator_suggestions",
    title: "Moderator Suggestions",
    purpose: "Generate candidate moderation questions from an eligible answer-block checkpoint.",
    editableLabel: "System instructions for the moderation assistant",
    systemDefault: MODERATOR_SUGGESTIONS_SYSTEM,
    userPromptSections: [
      {
        label: "Session context",
        template: "Session purpose:\n{purpose}\n\nIntended theme coverage:\n- {theme}\n\nSpeaker bios / roles:\n- {name}: {bio}",
        runtimeInputs: ["session.generation.purpose", "session.generation.themes", "session.generation.panelists"],
      },
      {
        label: "Current checkpoint",
        template: "Checkpoint: {checkpointKind}\nCurrent answer block: {speaker} - {duration}s - {wordCount} words\n{speaker}: {text}",
        runtimeInputs: ["checkpoint.kind", "answerBlock.speaker", "answerBlock.durationMs", "answerBlock.wordCount", "answerBlock.entries"],
      },
      {
        label: "Recent answer-block history",
        template: "Recent answer-block history:\n[{speaker}]\n{speaker}: {text}",
        runtimeInputs: ["recent answer blocks"],
      },
      {
        label: "Conversation summary (continuity / dedupe context)",
        template: "Conversation summary from live recaps (for continuity and dedupe — the current moment above takes priority):\n{conversationSummary}",
        runtimeInputs: ["recent live recap sections"],
      },
      {
        label: "Recent session suggestions",
        template: "Recent session suggestions:\n- [{status}] {prompt}",
        runtimeInputs: ["bounded recent session suggestions"],
      },
      {
        label: "Return instruction",
        template: "Return zero or up to {candidateCount} candidate suggestions as JSON.",
        runtimeInputs: ["candidate count"],
      },
    ],
    runtimeInputs: [
      "session purpose",
      "intended theme coverage",
      "speaker roster",
      "conversation summary from live recap sections",
      "current checkpoint kind",
      "current answer block",
      "recent substantial answer blocks",
      "bounded recent session suggestions",
      "candidate count",
    ],
    outputSchema: [
      "suggestions[].type",
      "suggestions[].title",
      "suggestions[].prompt",
      "suggestions[].rationale",
      "suggestions[].evidence[]",
      "topics[]?",
    ],
    providerOptions: [
      ...GROQ_STRUCTURED_PROVIDER_OPTIONS,
      { label: "temperature", value: "GROQ_TEMPERATURE env or 0.6" },
      { label: "schemaName", value: "ModeratorSuggestions" },
    ],
    requestPreview: moderatorSuggestionRequestPreview(1),
    requestPreviews: [
      {
        label: "Judge off",
        description: "One generation call requests zero or one suggestion. No judge call runs.",
        candidateCount: 1,
        schemaMaxItems: 1,
        requestPreview: moderatorSuggestionRequestPreview(1),
      },
      {
        label: "Judge on",
        description: "Generation requests up to three candidates, then a separate LLM judge selects or vetoes them.",
        candidateCount: 3,
        schemaMaxItems: 3,
        requestPreview: moderatorSuggestionRequestPreview(3),
      },
    ],
    requiredAdditions: [
      {
        id: "judge-off-quality-gate",
        label: "Judge-off quality gate",
        body: MODERATOR_SUGGESTION_QUALITY_GATE,
        reason: "When the judge is off, the generator must prefer silence over a generic or repetitive question.",
      },
    ],
  },
  {
    key: MODERATOR_SUGGESTION_JUDGE_KEY,
    workflow: "moderator_suggestion_judge",
    title: "Moderator Suggestion Judge",
    purpose: "Judge filtered moderator-question candidates and choose one winner or veto all.",
    editableLabel: "System instructions for the moderation judge",
    systemDefault: MODERATOR_SUGGESTION_JUDGE_SYSTEM,
    userPromptSections: [
      {
        label: "Session context",
        template: "Session purpose:\n{purpose}\n\nIntended theme coverage:\n- {theme}",
        runtimeInputs: ["session.generation.purpose", "session.generation.themes"],
      },
      {
        label: "Current checkpoint",
        template: "Current checkpoint: {checkpointKind}\n\nCurrent answer block:\n{speaker} - {durationSeconds}s - {wordCount} words\n{text}",
        runtimeInputs: ["checkpoint.kind", "answerBlock.speaker", "answerBlock.durationMs", "answerBlock.wordCount", "answerBlock.entries"],
      },
      {
        label: "Conversation summary",
        template: "Conversation summary from live recaps (for continuity and dedupe — the current moment above takes priority):\n{conversationSummary}",
        runtimeInputs: ["recent live recap sections"],
      },
      {
        label: "Recent session suggestions",
        template: "Recent session suggestions:\n- [{status}] {prompt}",
        runtimeInputs: ["bounded recent session suggestions"],
      },
      {
        label: "Candidates",
        template: "Candidates to judge:\n[index {index}] type={type}\nprompt: {prompt}\nrationale: {rationale}\nevidence:\n- {evidence}",
        runtimeInputs: ["filtered suggestion candidates"],
      },
      {
        label: "Return instruction",
        template: "Return one verdict per candidate. Then list at most two approved indexes, strongest first — approve two only when they open clearly different directions. Return an empty approved array to veto all. JSON only.",
        runtimeInputs: [],
      },
    ],
    runtimeInputs: [
      "session purpose",
      "intended theme coverage",
      "current checkpoint kind",
      "current answer block",
      "conversation summary from live recap sections",
      "bounded recent session suggestions",
      "filtered suggestion candidates",
    ],
    outputSchema: [
      "verdicts[].index",
      "verdicts[].pass",
      "verdicts[].reason",
      "approved[]",
    ],
    providerOptions: [
      ...GROQ_STRUCTURED_PROVIDER_OPTIONS,
      { label: "temperature", value: "0" },
      { label: "schemaName", value: "ModeratorSuggestionJudge" },
    ],
    requestPreview: [
      "system: <editable judge system instructions>",
      "prompt:",
      "Session purpose:",
      "<operator-provided purpose>",
      "",
      "Intended theme coverage:",
      "- <operator-provided theme>",
      "",
      "Current checkpoint: mid",
      "",
      "Current answer block:",
      "Panelist A - 92s - 240 words",
      "Panelist A: We kept testing prompts in isolation, but once duplicated instructions landed in the chain, the assistant started optimizing for the wrong behavior.",
      "",
      "Conversation summary from live recaps (for continuity and dedupe — the current moment above takes priority):",
      "[8:23 PM] Panelist A: Evaluation has to cover the full orchestration, not isolated prompts.",
      "",
      "Recent session suggestions:",
      "- [new] The point about the white-screen bug being a state-flow problem rather than a rendering issue — which state transition turned out to be the unstable one?",
      "",
      "Candidates to judge:",
      "[index 0] type=reasoning",
      "prompt: What changed once duplicated instructions landed in the chain?",
      "rationale: Opens the speaker's reasoning about full orchestration failures.",
      "evidence:",
      "- duplicated instructions landed in the chain",
      "",
      "Return one verdict per candidate. Then list at most two approved indexes, strongest first — approve two only when they open clearly different directions. Return an empty approved array to veto all. JSON only.",
    ].join("\n"),
  },
  {
    key: PANEL_RECAP_GENERATION_KEY,
    workflow: "panel_recap_generation",
    title: "Panel Recap Generation",
    purpose: "Extract interim panel-discussion recaps from finalized transcript windows.",
    editableLabel: "System instructions for panel recaps",
    systemDefault: PANEL_RECAP_GENERATION_SYSTEM,
    userPromptSections: [
      {
        label: "Session context",
        template: "Session:\n{sessionTitle}\n\nSession purpose:\n{purpose}\n\nIntended theme coverage:\n- {theme}\n\nSpeaker bios / roles:\n- {name}: {bio}",
        runtimeInputs: ["session.title", "session.generation.purpose", "session.generation.themes", "session.generation.panelists"],
      },
      {
        label: "Recap window",
        template: "Recap trigger: {triggerKind}\nSource window: {sourceStartTs} ({sourceStartTime}) to {sourceEndTs} ({sourceEndTime})",
        runtimeInputs: ["recap.triggerKind", "recap.sourceStartTs", "recap.sourceEndTs"],
      },
      {
        label: "Previous sections",
        template: "Previous recap sections:\n[ref {timeLabel} | ts={timeTs}] {speaker}\n- {point.text}\n  - {point.subpoint}",
        runtimeInputs: ["previous recap sections"],
      },
      {
        label: "Transcript window",
        template: "New transcript window:\n[{timeLabel} | ts={ts} | speakerKey={speakerKey}] {speaker}: {text}",
        runtimeInputs: ["transcript entries"],
      },
      {
        label: "Return instruction",
        template: 'Return a JSON object with exactly one top-level property: "sections".',
        runtimeInputs: [],
      },
    ],
    runtimeInputs: [
      "session title",
      "session purpose",
      "intended theme coverage",
      "speaker roster",
      "recap trigger kind",
      "previous recap sections",
      "source transcript window",
    ],
    outputSchema: [
      "sections[].timeTs",
      "sections[].timeLabel",
      "sections[].speaker",
      "sections[].speakerKey",
      "sections[].points[].text",
      "sections[].points[].subpoints[]",
    ],
    providerOptions: [
      ...GROQ_STRUCTURED_PROVIDER_OPTIONS,
      { label: "temperature", value: "0.2" },
      { label: "schemaName", value: "PanelRecapSections" },
    ],
    requestPreview: [
      "system: <editable system instructions>",
      "prompt:",
      "Session:",
      "<session title>",
      "",
      "Recap trigger: threshold",
      "Source window: 1720000000000 (8:26 PM) to 1720000180000 (8:29 PM)",
      "",
      "Previous recap sections:",
      "(none yet)",
      "",
      "New transcript window:",
      "[8:26 PM | ts=1720000000000 | speakerKey=speaker_1] Panelist A: <finalized transcript line>",
      "[8:27 PM | ts=1720000060000 | speakerKey=speaker_2] Panelist B: <finalized transcript line>",
      "",
      'Return a JSON object with exactly one top-level property: "sections".',
    ].join("\n"),
  },
  {
    key: PANEL_EPISODE_GENERATION_KEY,
    workflow: "panel_episode_generation",
    title: "Panel Episode Generation",
    purpose: "Extract one headline-and-claims artifact from a sealed episode.",
    editableLabel: "System instructions for panel episodes",
    systemDefault: PANEL_EPISODE_GENERATION_SYSTEM,
    userPromptSections: [
      {
        label: "Session context",
        template: "Session:\n{sessionTitle}\n\nSession purpose:\n{purpose}\n\nIntended theme coverage:\n- {theme}\n\nSpeaker bios / roles:\n- {name}: {bio}",
        runtimeInputs: ["session.title", "session.generation.purpose", "session.generation.themes", "session.generation.panelists"],
      },
      {
        label: "Claim ledger",
        template: "Claim ledger — recent claims already on the timeline. Reference these claimIds in \"relationKind\"/\"targetClaimId\":\n- [{claimId}] {speaker}: {text}",
        runtimeInputs: ["recent recap claims"],
      },
      {
        label: "Recent sections",
        template: "Recent published summary sections — continuity only; never re-extract from them:\n{continuitySections}",
        runtimeInputs: ["recent published summary sections"],
      },
      {
        label: "Episode transcript",
        template: "Episode transcript ({timeLabel}, sealed by {sealReason}):\n[{clockTime}] {speaker}: {text}",
        runtimeInputs: ["sealed episode transcript", "episode time label", "episode seal reason"],
      },
      {
        label: "Return instruction",
        template: "Return the JSON artifact for this episode only.",
        runtimeInputs: [],
      },
    ],
    runtimeInputs: [
      "session title",
      "session purpose",
      "intended theme coverage",
      "speaker roster",
      "recent claim ledger",
      "recent published summary sections",
      "sealed episode transcript",
      "episode seal reason",
    ],
    outputSchema: [
      "nonSubstantive",
      "headline",
      "claims[].text",
      "claims[].subpoints[]",
      "claims[].speaker",
      "claims[].relationKind",
      "claims[].targetClaimId",
    ],
    providerOptions: [
      ...GROQ_STRUCTURED_PROVIDER_OPTIONS,
      { label: "maxOutputTokens", value: "8192" },
      { label: "temperature", value: "0.2" },
      { label: "schemaName", value: "PanelEpisodeArtifact" },
    ],
    requestPreview: [
      "system: <editable system instructions>",
      "prompt:",
      "Session:",
      "<session title>",
      "",
      "Claim ledger — recent claims already on the timeline. Reference these claimIds in \"relationKind\"/\"targetClaimId\":",
      "(none yet)",
      "",
      "Recent published summary sections — continuity only; never re-extract from them:",
      "(none yet)",
      "",
      "Episode transcript (8:26 PM, sealed by sentence):",
      "[8:26 PM] Panelist A: <finalized transcript line>",
      "",
      "Return the JSON artifact for this episode only.",
    ].join("\n"),
  },
  {
    key: PANEL_RECAP_CONSOLIDATION_KEY,
    workflow: "panel_recap_consolidation",
    title: "Panel Recap Consolidation",
    purpose: "Produce a thematic consolidated summary with discussion arc and inline tensions.",
    editableLabel: "System instructions for recap consolidation",
    systemDefault: PANEL_RECAP_CONSOLIDATION_SYSTEM,
    userPromptSections: [
      {
        label: "Session context",
        template: "Session:\n{sessionTitle}\n\nSession purpose:\n{purpose}\n\nIntended theme coverage:\n- {theme}\n\nSpeaker bios / roles:\n- {name}: {bio}",
        runtimeInputs: ["session.title", "session.generation.purpose", "session.generation.themes", "session.generation.panelists"],
      },
      {
        label: "Consolidation scope",
        template: "Mode: {mode}\nConsolidate through ts: {throughTs}",
        runtimeInputs: ["consolidation mode", "latest recap sourceEndTs"],
      },
      {
        label: "Previous consolidation",
        template: "Previous consolidation output:\n{previousConsolidation}",
        runtimeInputs: ["previous narrative", "previous themes"],
      },
      {
        label: "Input recap sections",
        template: "A-tier recap sections:\n[{timeLabel} | ts={timeTs} | speakerKey={speakerKey}] {speaker}\n- {point.text}\n  - {point.subpoint}",
        runtimeInputs: ["live recap sections for selected mode"],
      },
      {
        label: "Transcript",
        template: "Transcript:\n[{timeLabel} | ts={ts} | speakerKey={speakerKey}] {speaker}: {text}",
        runtimeInputs: ["corrected transcript entries for selected mode"],
      },
      {
        label: "Return instruction",
        template: "Return JSON with structured narrative objects and themes arrays only.",
        runtimeInputs: [],
      },
    ],
    runtimeInputs: [
      "session title",
      "session purpose",
      "intended theme coverage",
      "speaker roster",
      "consolidation mode",
      "latest recap sourceEndTs",
      "previous consolidation output",
      "live recap sections for selected mode",
      "corrected transcript entries for selected mode",
    ],
    outputSchema: [
      "narrative[].point",
      "narrative[].subpoints[]",
      "themes[].title",
      "themes[].synthesis",
      "themes[].speakers[]",
      "themes[].tensions[].description",
      "themes[].tensions[].speakers[]",
    ],
    providerOptions: [
      { label: "model", value: `GROQ_MODEL env or ${DEFAULT_GROQ_MODEL}` },
      { label: "maxRetries", value: "1" },
      { label: "timeout", value: `${CONSOLIDATION_REQUEST_TIMEOUT_MS}ms` },
      { label: "maxOutputTokens", value: String(CONSOLIDATION_MAX_OUTPUT_TOKENS) },
      { label: "structuredOutputs", value: "true" },
      { label: "strictJsonSchema", value: "true" },
      { label: "reasoningEffort", value: "medium" },
      { label: "temperature", value: "0.2" },
      { label: "schemaName", value: "RecapConsolidation" },
    ],
    requestPreview: [
      "system: <editable system instructions>",
      "prompt:",
      "Session:",
      "<session title>",
      "",
      "Mode: incremental",
      "Consolidate through ts: 1720000360000",
      "",
      "Previous consolidation output:",
      "{ \"narrative\": [{ \"point\": \"Evaluation gave way to operational risk.\", \"subpoints\": [] }], \"themes\": [] }",
      "",
      "A-tier recap sections:",
      "[8:26 PM | ts=1720000000000 | speakerKey=speaker_1] Panelist A",
      "- Evaluation should cover the full orchestration.",
      "  - Isolated prompt checks missed duplicated instructions.",
      "",
      "Transcript:",
      "[8:26 PM | ts=1720000000000 | speakerKey=speaker_1] Panelist A: Evaluation should cover the full orchestration.",
      "",
      "Return JSON with the full updated structured narrative objects and themes arrays only.",
    ].join("\n"),
  },
  {
    key: AUDIENCE_POST_CATEGORIZATION_KEY,
    workflow: "audience_post_categorization",
    title: "Audience Post Categorization",
    purpose: "Assign each audience post to an existing or new category.",
    editableLabel: "System instructions for audience categorization",
    systemDefault: AUDIENCE_POST_CATEGORIZATION_SYSTEM,
    userPromptSections: [
      {
        label: "Session context",
        template: "Session:\n{sessionTitle}\n\nSession purpose:\n{purpose}\n\nIntended theme coverage:\n- {theme}",
        runtimeInputs: ["session.title", "session.generation.purpose", "session.generation.themes"],
      },
      {
        label: "Live context",
        template: "Latest consolidation themes:\n- {theme}\n\nCurrent answer block transcript:\n- {line}",
        runtimeInputs: ["recapConsolidation.themes", "current answer block transcript"],
      },
      {
        label: "Existing categories",
        template: "Existing categories:\n- {category.label}: {category.description}",
        runtimeInputs: ["audienceCategories.label", "audienceCategories.description"],
      },
      {
        label: "Candidate topics",
        template: "Candidate topics:\n- [{source}] {label}: {description}",
        runtimeInputs: ["audienceCategoryCandidates.source", "audienceCategoryCandidates.label", "audienceCategoryCandidates.description"],
      },
      {
        label: "Content",
        template: "Content to categorize:\n{body}",
        runtimeInputs: ["audience item body"],
      },
      {
        label: "Return instruction",
        template: 'Set "existingLabel" to one of the listed labels, or "newLabel" for a genuinely uncovered topic, as JSON.',
        runtimeInputs: ["minting allowed (category count vs cap)"],
      },
    ],
    runtimeInputs: [
      "session title",
      "session purpose",
      "intended theme coverage",
      "latest consolidation themes",
      "current answer block transcript",
      "current category labels",
      "current category descriptions",
      "candidate topic labels",
      "candidate topic descriptions",
      "audience item body",
    ],
    outputSchema: ["existingLabel", "newLabel", "description"],
    providerOptions: [
      ...GROQ_STRUCTURED_PROVIDER_OPTIONS,
      { label: "temperature", value: "0.2" },
      { label: "schemaName", value: "AudienceCategory" },
    ],
    requestPreview: [
      "system: <editable system instructions>",
      "prompt:",
      "Session:",
      "The Future of AI in Clinics",
      "",
      "Session purpose:",
      "Surface likely audience topics during a live panel.",
      "",
      "Latest consolidation themes:",
      "- Regulation",
      "",
      "Existing categories:",
      "- Questions: Audience questions for the panel.",
      "",
      "Candidate topics:",
      "- [seed] Clinical Safety: Posts about medical safety.",
      "",
      "Content to categorize:",
      "<participant post or AI suggestion body>",
      "",
      'Set "existingLabel" to one of the listed labels, or "newLabel" for a genuinely uncovered topic, as JSON.',
    ].join("\n"),
  },
  {
    key: AUDIENCE_CATEGORY_SEEDS_KEY,
    workflow: "audience_category_seeds",
    title: "Audience Category Seeds",
    purpose: "Generate non-canonical seed topic candidates from session setup metadata.",
    editableLabel: "System instructions for seed topic generation",
    systemDefault: AUDIENCE_CATEGORY_SEEDS_SYSTEM,
    userPromptSections: [
      {
        label: "Session context",
        template: "Session:\n{sessionTitle}\n\nSession purpose:\n{purpose}\n\nIntended theme coverage:\n- {theme}",
        runtimeInputs: ["session.title", "session.generation.purpose", "session.generation.themes"],
      },
      {
        label: "Return instruction",
        template: "Return 4 to 8 candidate audience categories as JSON with label and description.",
        runtimeInputs: [],
      },
    ],
    runtimeInputs: ["session title", "session purpose", "intended theme coverage"],
    outputSchema: ["candidates[].label", "candidates[].description"],
    providerOptions: [
      ...GROQ_STRUCTURED_PROVIDER_OPTIONS,
      { label: "temperature", value: "0.2" },
      { label: "schemaName", value: "AudienceCategorySeeds" },
    ],
    requestPreview: [
      "system: <editable system instructions>",
      "prompt:",
      "Session:",
      "The Future of AI in Clinics",
      "",
      "Session purpose:",
      "Generate likely audience topic seeds.",
      "",
      "Intended theme coverage:",
      "- Clinical safety",
      "- Drug access",
      "",
      "Return 4 to 8 candidate audience categories as JSON with label and description.",
    ].join("\n"),
  },
  {
    key: AUDIENCE_ARTIFACT_GENERATION_KEY,
    workflow: "audience_artifact_generation",
    title: "Audience Artifact Generation",
    purpose: "Synthesize category summaries or consolidated audience prompts for staff review.",
    editableLabel: "System instructions for audience synthesis",
    systemDefault: AUDIENCE_ARTIFACT_GENERATION_SYSTEM,
    userPromptSections: [
      {
        label: "Purpose",
        template: "{artifactPurpose}",
        runtimeInputs: ["artifact.kind"],
      },
      {
        label: "Category",
        template: "Category: {categoryLabel | all audience posts}",
        runtimeInputs: ["category.label"],
      },
      {
        label: "Audience posts",
        template: "Audience posts:\n{index}. {body} (likes: {likeCount}, replies: {replyCount})",
        runtimeInputs: ["posts.body", "posts.likeCount", "posts.replyCount"],
      },
      {
        label: "Return instruction",
        template: "Return markdown suitable for staff review.",
        runtimeInputs: [],
      },
    ],
    runtimeInputs: ["artifact.kind", "category.label", "audience posts", "like counts", "reply counts"],
    outputSchema: ["body"],
    providerOptions: [
      ...GROQ_STRUCTURED_PROVIDER_OPTIONS,
      { label: "temperature", value: "0.25" },
      { label: "schemaName", value: "AudienceArtifact" },
    ],
    requestPreview: [
      "system: <editable system instructions>",
      "prompt:",
      "Summarize the main audience themes in this category for an admin/moderator.",
      "Category: Questions",
      "",
      "Audience posts:",
      "1. <audience post> (likes: 3, replies: 1)",
      "",
      "Return markdown suitable for staff review.",
    ].join("\n"),
  },
];

export function isPromptContractKey(key: string): key is PromptContractKey {
  return PROMPT_CONTRACT_KEYS.includes(key as PromptContractKey);
}

export function isPromptWorkflowKey(value: string): value is PromptWorkflowKey {
  return PROMPT_CONTRACTS.some((contract) => contract.workflow === value);
}

export function getPromptContract(key: PromptContractKey): PromptContract {
  const contract = PROMPT_CONTRACTS.find((candidate) => candidate.key === key);
  if (!contract) throw new Error(`Unknown prompt contract: ${key}`);
  return contract;
}

export function getPromptContractByWorkflow(workflow: PromptWorkflowKey): PromptContract {
  const contract = PROMPT_CONTRACTS.find((candidate) => candidate.workflow === workflow);
  if (!contract) throw new Error(`Unknown prompt workflow: ${workflow}`);
  return contract;
}

export const DEFAULT_PROMPT_WORKFLOW = PROMPT_CONTRACTS[0].workflow;

export function resolvePromptBody(
  bodies: ResolvedPromptBodies | undefined,
  key: PromptContractKey,
): string {
  return bodies?.[key] ?? getPromptContract(key).systemDefault;
}

export function buildAudienceCategoryUserPrompt(input: {
  body: string;
  sessionTitle: string;
  purpose: string;
  themes: string[];
  latestConsolidationThemes: string[];
  currentBlockTranscript: string[];
  categories: Array<{ label: string; description?: string }>;
  candidates: Array<{ label: string; description?: string; source: "seed" | "topic" }>;
  mintingAllowed: boolean;
}): string {
  return [
    "Session:",
    input.sessionTitle.trim() || "(untitled session)",
    "",
    "Session purpose:",
    input.purpose.trim() || "(none provided)",
    "",
    "Intended theme coverage:",
    input.themes.length ? input.themes.map((theme) => "- " + theme).join("\n") : "- (none provided)",
    "",
    "Latest consolidation themes:",
    input.latestConsolidationThemes.length
      ? input.latestConsolidationThemes.map((theme) => "- " + theme).join("\n")
      : "- (none yet)",
    "",
    "Current answer block transcript:",
    input.currentBlockTranscript.length
      ? input.currentBlockTranscript.map((line) => "- " + line).join("\n")
      : "- (none available)",
    "",
    "Existing categories:",
    input.categories.length
      ? input.categories
          .map((category) => `- ${category.label}: ${category.description ?? ""}`.trim())
          .join("\n")
      : "- General",
    "",
    "Candidate topics:",
    input.candidates.length
      ? input.candidates
          .map((candidate) => `- [${candidate.source}] ${candidate.label}: ${candidate.description ?? ""}`.trim())
          .join("\n")
      : "- (none)",
    "",
    "Content to categorize:",
    input.body,
    "",
    input.mintingAllowed
      ? 'Set "existingLabel" to one of the listed labels, or "newLabel" for a genuinely uncovered topic, as JSON.'
      : 'The category set is full: set "existingLabel" to the closest listed label as JSON.',
  ].join("\n");
}

export function buildAudienceCategorySeedUserPrompt(input: {
  sessionTitle: string;
  purpose: string;
  themes: string[];
}): string {
  return [
    "Session:",
    input.sessionTitle.trim() || "(untitled session)",
    "",
    "Session purpose:",
    input.purpose.trim() || "(none provided)",
    "",
    "Intended theme coverage:",
    input.themes.length ? input.themes.map((theme) => "- " + theme).join("\n") : "- (none provided)",
    "",
    "Return 4 to 8 candidate audience categories as JSON with label and description.",
  ].join("\n");
}

export function buildAudienceArtifactUserPrompt(input: {
  kind: "category-summary" | "consolidation-suggestion";
  categoryLabel?: string;
  posts: Array<{ body: string; likeCount: number; replyCount: number }>;
}): string {
  const purpose =
    input.kind === "category-summary"
      ? "Summarize the main audience themes in this category for an admin/moderator."
      : "Suggest concise consolidated questions or comments that represent the audience discussion.";

  return [
    purpose,
    input.categoryLabel ? `Category: ${input.categoryLabel}` : "Category: all audience posts",
    "",
    "Audience posts:",
    input.posts
      .map(
        (post, index) =>
          `${index + 1}. ${post.body} (likes: ${post.likeCount}, replies: ${post.replyCount})`,
      )
      .join("\n"),
    "",
    "Return markdown suitable for staff review.",
  ].join("\n");
}
